(function () {
  'use strict';

  const STORAGE_KEY = 'shopify_wishlist_items';
  const DRAWER_OPEN_CLASS = 'wishlist-drawer-open';
  const FILLED_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.27 2 8.5 2 5.41 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.08C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.41 22 8.5c0 3.77-3.4 6.86-8.55 11.53L12 21.35z"/></svg>';
  const OUTLINE_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="20" height="20"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';

  let wishlistItems = [];
  let eventsBound = false;
  let activeDrawer = null;

  function init() {
    loadFromStorage();
    renderAllButtons(document);
    updateCountBadges();
    renderWishlistPage();
    renderWishlistDrawers();

    if (!eventsBound) {
      bindEvents();
      eventsBound = true;
    }
  }

  function loadFromStorage() {
    wishlistItems = readStoredItems();
  }

  function readStoredItems() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map((item) => normalizeProductData(item.id, item))
        .filter((item) => item && item.id);
    } catch (error) {
      return [];
    }
  }

  function saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlistItems));
    } catch (error) {
      console.warn('Wishlist storage is unavailable.');
    }
  }

  function normalizeProductData(productId, productData) {
    if (!productId) {
      return null;
    }

    const data = productData || {};

    return {
      id: String(productId),
      title: String(data.title || '').trim(),
      url: String(data.url || '').trim(),
      image: String(data.image || '').trim(),
      price: String(data.price || '').trim(),
      vendor: String(data.vendor || '').trim(),
      available: data.available !== false && data.available !== 'false',
      addedAt: Number(data.addedAt) || Date.now(),
    };
  }

  function hasProduct(productId) {
    return wishlistItems.some((item) => item.id === String(productId));
  }

  function addToWishlist(productId, productData) {
    if (hasProduct(productId)) {
      return false;
    }

    const normalized = normalizeProductData(productId, productData);

    if (!normalized) {
      return false;
    }

    wishlistItems = [normalized, ...wishlistItems];
    persistAndRefresh();
    showToast('Added to wishlist.');
    dispatchWishlistEvent('wishlist:added', { productId: normalized.id, product: normalized });
    return true;
  }

  function removeFromWishlist(productId, options) {
    const settings = options || {};
    const normalizedId = String(productId);
    const originalLength = wishlistItems.length;

    wishlistItems = wishlistItems.filter((item) => item.id !== normalizedId);

    if (wishlistItems.length === originalLength) {
      return false;
    }

    persistAndRefresh();

    if (!settings.silentToast) {
      showToast('Removed from wishlist.', { source: settings.source });
    }

    dispatchWishlistEvent('wishlist:removed', { productId: normalizedId });
    return true;
  }

  function toggleWishlist(productId, productData) {
    if (hasProduct(productId)) {
      removeFromWishlist(productId);
      return false;
    }

    addToWishlist(productId, productData);
    return true;
  }

  function clearWishlist(source) {
    wishlistItems = [];
    persistAndRefresh();
    showToast('Wishlist cleared.', { source });
    dispatchWishlistEvent('wishlist:cleared', {});
  }

  function persistAndRefresh() {
    saveToStorage();
    renderAllButtons(document);
    updateCountBadges();
    renderWishlistPage();
    renderWishlistDrawers();
    dispatchWishlistEvent('wishlist:updated', {
      count: wishlistItems.length,
      items: wishlistItems.slice(),
    });
  }

  function renderAllButtons(root) {
    const scope = root || document;
    const buttons = scope.querySelectorAll('[data-wishlist-btn]');

    buttons.forEach((button) => {
      bindWishlistButton(button);
      const productId = button.getAttribute('data-product-id');
      setButtonState(button, hasProduct(productId));
    });
  }

  function bindWishlistButton(button) {
    if (!button || button.dataset.wishlistBound === 'true') {
      return;
    }

    button.dataset.wishlistBound = 'true';
    button.addEventListener('pointerdown', handleWishlistButtonPointerDown);
    button.addEventListener('click', handleWishlistButtonClick);
  }

  function handleWishlistButtonPointerDown(event) {
    event.stopPropagation();
  }

  function handleWishlistButtonClick(event) {
    const button = event.currentTarget;

    if (!button) {
      return;
    }

    toggleWishlistFromButton(button, event);
  }

  function toggleWishlistFromButton(button, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const productId = button.getAttribute('data-product-id');
    toggleWishlist(productId, extractProductDataFromButton(button));
  }

  function extractProductDataFromButton(button) {
    return {
      title: button.getAttribute('data-product-title'),
      url: button.getAttribute('data-product-url'),
      image: button.getAttribute('data-product-image'),
      price: button.getAttribute('data-product-price'),
      vendor: button.getAttribute('data-product-vendor'),
      available: button.getAttribute('data-product-available') !== 'false',
    };
  }

  function setButtonState(button, isActive) {
    button.classList.toggle('wishlist-active', isActive);
    button.setAttribute('aria-pressed', String(isActive));
    button.setAttribute('aria-label', isActive ? 'Remove from wishlist' : 'Add to wishlist');
    button.title = isActive ? 'Remove from wishlist' : 'Add to wishlist';

    const icon = button.querySelector('.wishlist-icon');

    if (icon) {
      icon.innerHTML = isActive ? FILLED_ICON : OUTLINE_ICON;
    } else {
      button.innerHTML = isActive ? FILLED_ICON : OUTLINE_ICON;
    }
  }

  function updateCountBadges() {
    const count = wishlistItems.length;
    const displayCount = count > 99 ? '99+' : String(count);

    document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
      badge.textContent = displayCount;

      if (count > 0) {
        badge.style.removeProperty('display');
      } else {
        badge.style.display = 'none';
      }
    });

    document.querySelectorAll('[data-wishlist-total]').forEach((total) => {
      total.textContent = String(count);
    });
  }

  function bindEvents() {
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleDocumentKeydown);
    document.addEventListener('shopify:section:load', handleSectionLifecycle);
    document.addEventListener('shopify:section:reorder', handleSectionLifecycle);
    document.addEventListener('shopify:section:select', handleSectionLifecycle);
    document.addEventListener('wishlist:reinit', function () {
      renderAllButtons(document);
      updateCountBadges();
      renderWishlistPage();
      renderWishlistDrawers();
    });
    window.addEventListener('storage', handleStorageSync);
  }

  function handleDocumentClick(event) {
    if (event.defaultPrevented) {
      return;
    }

    const drawerCloseButton = event.target.closest('[data-wishlist-drawer-close]');

    if (drawerCloseButton) {
      event.preventDefault();
      closeWishlistDrawer(drawerCloseButton.closest('[data-wishlist-drawer]'));
      return;
    }

    const wishlistLink = event.target.closest('[data-wishlist-link]');

    if (wishlistLink) {
      const handled = handleWishlistLinkClick(wishlistLink, event);
      if (handled) {
        return;
      }
    }

    const wishlistButton = event.target.closest('[data-wishlist-btn]');

    if (wishlistButton) {
      toggleWishlistFromButton(wishlistButton, event);
      return;
    }

    const removeButton = event.target.closest('[data-wishlist-remove]');

    if (removeButton) {
      event.preventDefault();
      removeFromWishlist(removeButton.getAttribute('data-product-id'), { source: removeButton });
      return;
    }

    const clearButton = event.target.closest('[data-wishlist-clear]');

    if (clearButton) {
      event.preventDefault();

      if (window.confirm('Clear all wishlist items?')) {
        clearWishlist(clearButton);
      }
      return;
    }

    const addToCartButton = event.target.closest('[data-wishlist-add-to-cart]');

    if (addToCartButton) {
      event.preventDefault();
      ajaxAddToCart(addToCartButton);
    }
  }

  function handleDocumentKeydown(event) {
    if (event.key === 'Escape' || event.code === 'Escape') {
      closeWishlistDrawer();
    }
  }

  function handleSectionLifecycle(event) {
    const target = event && event.target ? event.target : document;

    window.setTimeout(function () {
      renderAllButtons(target);
      updateCountBadges();
      renderWishlistPage();
      renderWishlistDrawers();
    }, 100);
  }

  function handleStorageSync(event) {
    if (event.key && event.key !== STORAGE_KEY) {
      return;
    }

    loadFromStorage();
    renderAllButtons(document);
    updateCountBadges();
    renderWishlistPage();
    renderWishlistDrawers();
  }

  function handleWishlistLinkClick(link, event) {
    const settings = getWishlistSettings(link);

    if (settings.wishlistMode !== 'drawer') {
      return false;
    }

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    openWishlistDrawer(link);
    return true;
  }

  function getSectionRoot(source) {
    if (!source || typeof source.closest !== 'function') {
      return null;
    }

    return source.closest('.mm-section') || source.closest('.shopify-section');
  }

  function getWishlistShell(source) {
    const sectionRoot = getSectionRoot(source);

    if (sectionRoot) {
      const shellInSection = sectionRoot.querySelector('[data-mm-shell]');
      if (shellInSection) {
        return shellInSection;
      }
    }

    if (source && typeof source.closest === 'function') {
      const closestShell = source.closest('[data-mm-shell]');
      if (closestShell) {
        return closestShell;
      }
    }

    return document.querySelector('[data-mm-shell]');
  }

  function getWishlistDrawer(source) {
    const sectionRoot = getSectionRoot(source);

    if (sectionRoot) {
      const drawer = sectionRoot.querySelector('[data-wishlist-drawer]');
      if (drawer) {
        return drawer;
      }
    }

    return document.querySelector('[data-wishlist-drawer]');
  }

  function getWishlistSettings(source) {
    const shell = getWishlistShell(source);
    const sourceMode = source && source.getAttribute ? source.getAttribute('data-wishlist-mode') : '';
    const sourceFeedback = source && source.getAttribute ? source.getAttribute('data-wishlist-feedback') : '';

    return {
      wishlistMode: sourceMode || (shell && shell.dataset ? shell.dataset.wishlistMode : '') || 'page',
      wishlistFeedback: sourceFeedback || (shell && shell.dataset ? shell.dataset.wishlistFeedback : '') || 'popup',
      addToCartBehavior:
        (shell && shell.dataset ? shell.dataset.wishlistAddToCartBehavior : '') || 'theme',
      drawerTitle: (shell && shell.dataset ? shell.dataset.wishlistDrawerTitle : '') || 'My Wishlist',
      wishlistPageUrl: (shell && shell.dataset ? shell.dataset.wishlistPageUrl : '') || '/pages/wishlist',
      themeCartType: (shell && shell.dataset ? shell.dataset.mmThemeCartType : '') || 'page',
      cartUrl:
        (shell && shell.dataset ? shell.dataset.mmCartUrl : '') ||
        (window.routes && window.routes.cart_url) ||
        '/cart',
    };
  }

  function renderWishlistPage() {
    const container = document.getElementById('wishlist-page-container');

    if (!container) {
      return;
    }

    if (wishlistItems.length === 0) {
      container.innerHTML =
        '<div class="wishlist-empty">' +
        '<div class="wishlist-empty-icon">' +
        OUTLINE_ICON +
        '</div>' +
        '<h2>Your wishlist is empty.</h2>' +
        '<p>Save the products you love and find them here later.</p>' +
        '<a class="wishlist-btn-shop" href="/collections/all">Continue shopping</a>' +
        '</div>';
      return;
    }

    const countLabel = wishlistItems.length === 1 ? 'item' : 'items';
    const cardsMarkup = wishlistItems.map((item) => renderWishlistCard(item)).join('');

    container.innerHTML =
      '<div class="wishlist-header">' +
      '<h1>My Wishlist <span class="wishlist-count-text">(' +
      wishlistItems.length +
      ' ' +
      countLabel +
      ')</span></h1>' +
      '<button class="wishlist-clear-all" type="button" data-wishlist-clear>Clear all</button>' +
      '</div>' +
      '<div class="wishlist-grid">' +
      cardsMarkup +
      '</div>';
  }

  function renderWishlistDrawers() {
    document.querySelectorAll('[data-wishlist-drawer]').forEach((drawer) => {
      const body = drawer.querySelector('[data-wishlist-drawer-body]');
      const pageLink = drawer.querySelector('[data-wishlist-page-link]');
      const settings = getWishlistSettings(drawer);

      if (pageLink && settings.wishlistPageUrl) {
        pageLink.setAttribute('href', settings.wishlistPageUrl);
      }

      if (!body) {
        return;
      }

      if (wishlistItems.length === 0) {
        body.innerHTML =
          '<div class="mm-wishlist-drawer__empty">' +
          '<div class="mm-wishlist-drawer__empty-icon">' +
          OUTLINE_ICON +
          '</div>' +
          '<h3>Your wishlist is empty.</h3>' +
          '<p>Tap the heart on any product to save it here instantly.</p>' +
          '<a href="/collections/all" class="button button--primary">Continue shopping</a>' +
          '</div>';
        return;
      }

      const countLabel = wishlistItems.length === 1 ? 'item' : 'items';
      const cardsMarkup = wishlistItems.map((item) => renderWishlistDrawerCard(item)).join('');

      body.innerHTML =
        '<div class="mm-wishlist-drawer__summary">' +
        '<p><strong>' +
        wishlistItems.length +
        '</strong> saved ' +
        countLabel +
        '</p>' +
        '<button type="button" class="mm-wishlist-drawer__clear" data-wishlist-clear>Clear all</button>' +
        '</div>' +
        '<div class="mm-wishlist-drawer__items">' +
        cardsMarkup +
        '</div>';
    });
  }

  function renderWishlistCard(item) {
    const title = escapeHtml(item.title || 'Untitled product');
    const url = escapeAttribute(item.url || '#');
    const vendor = item.vendor ? '<p class="wishlist-vendor">' + escapeHtml(item.vendor) + '</p>' : '';
    const price = item.price ? '<p class="wishlist-price">' + escapeHtml(item.price) + '</p>' : '';
    const soldOutBadge = item.available ? '' : '<span class="wishlist-badge-oos">Sold out</span>';
    const addToCartText = item.available ? 'Add to cart' : 'Sold out';
    const addToCartDisabled = item.available ? '' : ' disabled';

    return (
      '<div class="wishlist-product-card" data-wishlist-card="' +
      escapeAttribute(item.id) +
      '">' +
      '<a href="' +
      url +
      '" class="wishlist-card-img-wrap">' +
      renderWishlistCardMedia(item) +
      soldOutBadge +
      '</a>' +
      '<div class="wishlist-card-info">' +
      vendor +
      '<a href="' +
      url +
      '" class="wishlist-product-title">' +
      title +
      '</a>' +
      price +
      '<div class="wishlist-card-actions">' +
      '<button class="wishlist-add-to-cart-btn" type="button" data-wishlist-add-to-cart data-product-id="' +
      escapeAttribute(item.id) +
      '" data-product-url="' +
      url +
      '"' +
      addToCartDisabled +
      '>' +
      addToCartText +
      '</button>' +
      '<button class="wishlist-remove-btn" type="button" data-wishlist-remove data-product-id="' +
      escapeAttribute(item.id) +
      '" aria-label="Remove from wishlist">' +
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>' +
      '</button>' +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  function renderWishlistDrawerCard(item) {
    const title = escapeHtml(item.title || 'Untitled product');
    const url = escapeAttribute(item.url || '#');
    const vendor = item.vendor
      ? '<p class="mm-wishlist-drawer__vendor">' + escapeHtml(item.vendor) + '</p>'
      : '';
    const price = item.price
      ? '<p class="mm-wishlist-drawer__price">' + escapeHtml(item.price) + '</p>'
      : '';
    const addToCartText = item.available ? 'Add to cart' : 'Sold out';
    const addToCartDisabled = item.available ? '' : ' disabled';

    return (
      '<div class="mm-wishlist-drawer__item" data-wishlist-card="' +
      escapeAttribute(item.id) +
      '">' +
      '<a href="' +
      url +
      '" class="mm-wishlist-drawer__media">' +
      renderWishlistCardMedia(item) +
      '</a>' +
      '<div class="mm-wishlist-drawer__content">' +
      vendor +
      '<a href="' +
      url +
      '" class="mm-wishlist-drawer__name">' +
      title +
      '</a>' +
      price +
      '<div class="mm-wishlist-drawer__actions">' +
      '<button class="mm-wishlist-drawer__atc" type="button" data-wishlist-add-to-cart data-product-id="' +
      escapeAttribute(item.id) +
      '" data-product-url="' +
      url +
      '"' +
      addToCartDisabled +
      '>' +
      addToCartText +
      '</button>' +
      '<button class="mm-wishlist-drawer__remove" type="button" data-wishlist-remove data-product-id="' +
      escapeAttribute(item.id) +
      '" aria-label="Remove from wishlist">Remove</button>' +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  function renderWishlistCardMedia(item) {
    if (item.image) {
      return (
        '<img src="' +
        escapeAttribute(item.image) +
        '" alt="' +
        escapeAttribute(item.title || 'Wishlist product') +
        '" loading="lazy">'
      );
    }

    return '<div class="wishlist-card-placeholder">' + OUTLINE_ICON + '</div>';
  }

  function openWishlistDrawer(source) {
    const drawer = getWishlistDrawer(source);

    if (!drawer) {
      return false;
    }

    renderWishlistDrawers();
    activeDrawer = drawer;
    drawer.hidden = false;

    window.requestAnimationFrame(function () {
      drawer.classList.add('is-open');
      syncWishlistDrawerLock();
      const closeButton = drawer.querySelector('[data-wishlist-drawer-close]');
      if (closeButton) {
        closeButton.focus();
      }
    });

    return true;
  }

  function closeWishlistDrawer(drawerElement) {
    const drawer =
      drawerElement ||
      activeDrawer ||
      document.querySelector('[data-wishlist-drawer].is-open');

    if (!drawer) {
      return;
    }

    drawer.classList.remove('is-open');

    if (activeDrawer === drawer) {
      activeDrawer = null;
    }

    window.setTimeout(function () {
      if (!drawer.classList.contains('is-open')) {
        drawer.hidden = true;
      }
      syncWishlistDrawerLock();
    }, 220);

    syncWishlistDrawerLock();
  }

  function syncWishlistDrawerLock() {
    const hasOpenDrawer = Boolean(document.querySelector('[data-wishlist-drawer].is-open'));
    document.documentElement.classList.toggle(DRAWER_OPEN_CLASS, hasOpenDrawer);
    document.body.classList.toggle(DRAWER_OPEN_CLASS, hasOpenDrawer);
  }

  async function ajaxAddToCart(button) {
    const productUrl = button.getAttribute('data-product-url');
    const productId = button.getAttribute('data-product-id');
    const originalText = button.textContent;
    const settings = getWishlistSettings(button);
    const effectiveMode = resolveWishlistCartMode(settings.addToCartBehavior, settings.themeCartType);

    if (!productUrl) {
      return;
    }

    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const variantId = await resolveVariantId(productUrl);
      const result = await addVariantToCart({
        variantId,
        mode: effectiveMode,
        trigger: button,
      });

      await refreshCartUI();

      if (productId) {
        removeFromWishlist(productId, {
          silentToast: true,
          source: button,
        });
      }

      if (result.cart && typeof result.cart.renderContents === 'function') {
        if (result.cart.classList && result.cart.classList.contains('is-empty')) {
          result.cart.classList.remove('is-empty');
        }

        result.cart.renderContents(result.payload);
      } else if (effectiveMode === 'drawer' || effectiveMode === 'notification') {
        const opened = await openThemeCartSurface(button);

        if (!opened) {
          showToast('Added to cart.', { force: true, source: button });
        }
      } else if (effectiveMode === 'stay') {
        showToast('Added to cart.', { force: true, source: button });
      }

      if (effectiveMode === 'page') {
        showToast('Moved to cart.', { force: true, source: button });
        button.textContent = 'Opening cart...';
        window.location = settings.cartUrl;
        return;
      }

      showToast('Moved to cart.', { force: true, source: button });
      button.textContent = 'Added';

      window.setTimeout(function () {
        button.disabled = false;
        button.textContent = originalText;
      }, 1400);
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      showToast('Could not add this item to cart.', { force: true, source: button });
      console.error(error);
    }
  }

  function resolveWishlistCartMode(preferredMode, themeCartType) {
    if (!preferredMode || preferredMode === 'theme') {
      return themeCartType || 'page';
    }

    return preferredMode;
  }

  function getThemeCartComponent(mode) {
    if (mode === 'notification') {
    return document.querySelector('cart-drawer');
    }

    if (mode === 'drawer') {
    return document.querySelector('cart-drawer');
    }

    return null;
  }

  async function addVariantToCart(options) {
    const settings = options || {};
    const variantId = settings.variantId;
    const mode = settings.mode || 'stay';
    const trigger = settings.trigger || null;
    const cartComponent = getThemeCartComponent(mode);

    if (
      (mode === 'drawer' || mode === 'notification') &&
      cartComponent &&
      typeof cartComponent.getSectionsToRender === 'function' &&
      typeof cartComponent.renderContents === 'function'
    ) {
      return addVariantToCartWithSections(variantId, cartComponent, trigger);
    }

    return addVariantToCartWithJson(variantId);
  }

  async function addVariantToCartWithSections(variantId, cartComponent, trigger) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append(
      'sections',
      cartComponent.getSectionsToRender().map(function (section) {
        return section.id;
      })
    );
    formData.append('sections_url', window.location.pathname);

    if (typeof cartComponent.setActiveElement === 'function') {
      cartComponent.setActiveElement(trigger || document.activeElement);
    }

    const response = await fetch(
      (window.routes && window.routes.cart_add_url) || '/cart/add.js',
      {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: formData,
      }
    );

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart add failed.');
    }

    return {
      payload: data,
      cart: cartComponent,
    };
  }

  async function addVariantToCartWithJson(variantId) {
    const response = await fetch(
      (window.routes && window.routes.cart_add_url) || '/cart/add.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart add failed.');
    }

    return {
      payload: data,
      cart: null,
    };
  }

  async function openThemeCartSurface(trigger) {
    if (window.MegaMenuCart && typeof window.MegaMenuCart.open === 'function') {
      try {
        return await window.MegaMenuCart.open(trigger);
      } catch (error) {
        return false;
      }
    }

    return false;
  }

  async function resolveVariantId(productUrl) {
    const normalizedUrl = productUrl.split('?')[0].replace(/\/$/, '');
    const response = await fetch(normalizedUrl + '.js', {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error('Product lookup failed.');
    }

    const product = await response.json();
    const availableVariant = product.variants.find((variant) => variant.available);
    const fallbackVariant = availableVariant || product.variants[0];

    if (!fallbackVariant) {
      throw new Error('No purchasable variant found.');
    }

    return fallbackVariant.id;
  }

  async function refreshCartUI() {
    try {
      const cartResponse = await fetch('/cart.js', {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!cartResponse.ok) {
        throw new Error('Unable to refresh cart.');
      }

      const cart = await cartResponse.json();
      updateNativeCartCounters(cart.item_count);
      updateMegaMenuCartCounters(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      if (window.MegaMenuCart && typeof window.MegaMenuCart.refreshBadges === 'function') {
        window.MegaMenuCart.refreshBadges();
      }
    } catch (error) {
      console.warn('Cart UI refresh skipped.', error);
    }
  }

  function updateNativeCartCounters(itemCount) {
    document
      .querySelectorAll('.cart-count-bubble span[aria-hidden="true"], [data-cart-count]')
      .forEach((element) => {
        element.textContent = itemCount > 99 ? '99+' : String(itemCount);
      });
  }

  function updateMegaMenuCartCounters(itemCount) {
    document.querySelectorAll('[data-mm-cart-button]').forEach((button) => {
      let badge;

      if (button.classList.contains('mm-mobile-bottom-nav__item')) {
        const iconWrap = button.querySelector('.mm-mobile-bottom-nav__icon');
        badge = button.querySelector('.mm-mobile-bottom-nav__badge');

        if (!badge && iconWrap) {
          badge = document.createElement('span');
          badge.className = 'mm-mobile-bottom-nav__badge';
          badge.setAttribute('aria-hidden', 'true');
          iconWrap.appendChild(badge);
        }
      } else {
        badge = button.querySelector('.mm-cart-badge');

        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'mm-cart-badge';
          badge.setAttribute('aria-hidden', 'true');
          button.appendChild(badge);
        }
      }

      if (!badge) {
        return;
      }

      badge.textContent = itemCount > 99 ? '99+' : String(itemCount);
      badge.style.display = itemCount > 0 ? '' : 'none';
    });
  }

  function showToast(message, options) {
    const settings = options || {};

    if (!settings.force) {
      const feedback = settings.feedback || getWishlistSettings(settings.source).wishlistFeedback;

      if (feedback === 'silent') {
        return;
      }
    }

    let toast = document.getElementById('wishlist-toast');

    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('wishlist-toast-show');

    window.clearTimeout(toast._wishlistTimer);
    toast._wishlistTimer = window.setTimeout(function () {
      toast.classList.remove('wishlist-toast-show');
    }, 2400);
  }

  function dispatchWishlistEvent(name, detail) {
    document.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: detail,
      })
    );
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttribute(value) {
    return escapeHtml(value);
  }

  window.Wishlist = {
    add: addToWishlist,
    remove: removeFromWishlist,
    toggle: toggleWishlist,
    has: hasProduct,
    getAll: function () {
      return wishlistItems.slice();
    },
    clear: clearWishlist,
    openDrawer: openWishlistDrawer,
    closeDrawer: closeWishlistDrawer,
    reinit: init,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
